/**
 * Abonamentul contului — plan, limite, facturi.
 *
 * Pagina răspunde la trei întrebări, în ordinea în care și le pune un proprietar de restaurant:
 *  1. Pe ce plan sunt și cât costă? (cu ce urmează să se întâmple la reînnoire)
 *  2. Mai am loc? (consumul real față de limitele planului, ca să nu descopere limita când e depășită)
 *  3. Ce am de plătit? (facturile, cu scadență și instrucțiuni de plată)
 *
 * Nimic nu este hardcodat în interfață: planurile, prețurile, limitele și funcționalitățile vin din
 * server (admin le poate schimba oricând), iar furnizorul de plată se detectează din configurare.
 */
import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  Icon,
  InlineAlert,
  Modal,
  PageHeader,
  Skeleton,
  cx,
  useToast,
} from '../components/ui.js';
import { api } from '../lib/api.js';
import { formatCurrency, formatDateTime, formatNumber, useResource } from '../lib/hooks.js';
import { errorMessage, useSession } from '../lib/session.js';

interface Subscription {
  id: string;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'paused';
  planId: string;
  planCode: string;
  planName: string;
  priceCents: number;
  currency: string;
  billingInterval: string;
  intervalCount: number;
  trialEndsAt: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  discountPercent: number | null;
}

interface UsageItem {
  key: string;
  label: string;
  limit: number;
  used: number;
}

interface Plan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  priceCents: number;
  currency: string;
  billingInterval: string;
  trialDays: number;
  features: string[];
  limits: Record<string, number>;
}

interface Invoice {
  id: string;
  number: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  currency: string;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
}

interface PaymentIntent {
  invoiceNumber: string;
  totalCents: number;
  currency: string;
  dueAt: string | null;
  provider: string;
  canPayOnline: boolean;
  redirectUrl: string | null;
  instructions: string | null;
  reason: string | null;
}

const STATUS_META: Record<Subscription['status'], { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' | 'info'; hint: string }> = {
  trialing: { label: 'Perioadă de probă', tone: 'info', hint: 'Ai acces complet, fără plată, până la data afișată.' },
  active: { label: 'Activ', tone: 'success', hint: 'Totul funcționează normal.' },
  past_due: { label: 'Plată în așteptare', tone: 'warning', hint: 'Contul funcționează în continuare; achită factura pentru a evita trecerea pe planul gratuit.' },
  canceled: { label: 'Anulat', tone: 'neutral', hint: 'Abonamentul se încheie la finalul perioadei plătite.' },
  paused: { label: 'În pauză', tone: 'warning', hint: 'Se aplică limitele planului gratuit. Datele rămân intacte.' },
};

const INVOICE_STATUS: Record<Invoice['status'], { label: string; tone: 'success' | 'warning' | 'neutral' | 'danger' }> = {
  draft: { label: 'ciornă', tone: 'neutral' },
  open: { label: 'de achitat', tone: 'warning' },
  paid: { label: 'achitată', tone: 'success' },
  void: { label: 'anulată', tone: 'neutral' },
  uncollectible: { label: 'neîncasabilă', tone: 'danger' },
};

const FEATURE_LABELS: Record<string, string> = {
  qr_menu: 'Meniu QR nelimitat',
  digital_display: 'Ecrane digitale',
  analytics: 'Statistici avansate',
  promotions: 'Promoții și reduceri',
  multi_location: 'Mai multe locații',
  multi_language: 'Meniu multilingv',
  ordering: 'Comenzi online',
  reservations: 'Rezervări',
  api: 'Acces API',
  white_label: 'Fără sigla Menuvia',
  custom_domain: 'Domeniu propriu (meniu. restaurantul-tău.ro)',
};

export function BillingPage() {
  const session = useSession();
  const toast = useToast();
  const canManage = session.permissions.includes('billing.manage');

  const subscription = useResource<{ subscription: Subscription | null; usage: { items: UsageItem[] } | null }>(
    () => api.get('/account/subscription'),
  );
  const invoices = useResource<{ invoices: Invoice[] }>(() => api.get('/account/invoices'));
  const plans = useResource<{ plans: Plan[] }>(() => api.get('/billing/plans'));

  const [changing, setChanging] = useState(false);
  const [planTarget, setPlanTarget] = useState<Plan | null>(null);
  const [paying, setPaying] = useState<Invoice | null>(null);
  const [payment, setPayment] = useState<PaymentIntent | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [busy, setBusy] = useState(false);

  const current = subscription.data?.subscription ?? null;
  const usage = subscription.data?.usage?.items ?? [];
  const statusMeta = current ? STATUS_META[current.status] : null;

  const sortedPlans = useMemo(() => [...(plans.data?.plans ?? [])].sort((a, b) => a.priceCents - b.priceCents), [plans.data]);
  const openInvoices = (invoices.data?.invoices ?? []).filter((invoice) => invoice.status === 'open');

  async function changePlan() {
    if (!planTarget) return;
    setBusy(true);
    try {
      await api.post('/account/subscription', { planCode: planTarget.code, reason: 'schimbare din dashboard' });
      toast.success('Plan actualizat', `Acum folosești planul ${planTarget.name}.`);
      setPlanTarget(null);
      await Promise.all([subscription.reload(), invoices.reload()]);
    } catch (error) {
      toast.error('Planul nu a putut fi schimbat', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function cancelSubscription() {
    setBusy(true);
    try {
      await api.post('/account/subscription/cancel', {});
      toast.success('Abonament anulat', 'Accesul rămâne complet până la finalul perioadei plătite.');
      setCancelling(false);
      await subscription.reload();
    } catch (error) {
      toast.error('Anularea nu a reușit', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  /** Repornirea abonamentului anulat: se anulează intenția de încheiere, fără schimbare de plan. */
  async function resumeSubscription() {
    setBusy(true);
    try {
      await api.post('/account/subscription', { planCode: current!.planCode, reason: 'anulare a anulării' });
      toast.success('Abonament reluat', 'Se reînnoiește automat la finalul perioadei.');
      await subscription.reload();
    } catch (error) {
      toast.error('Reluarea nu a reușit', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function openPayment(invoice: Invoice) {
    setPaying(invoice);
    setPayment(null);
    setPayLoading(true);
    try {
      const intent = await api.post<PaymentIntent>(`/account/invoices/${invoice.id}/pay`, {});
      setPayment(intent);
      if (intent.canPayOnline && intent.redirectUrl) {
        window.location.href = intent.redirectUrl;
      }
    } catch (error) {
      toast.error('Nu am putut pregăti plata', errorMessage(error));
    } finally {
      setPayLoading(false);
    }
  }

  async function downloadInvoice(invoice: Invoice) {
    try {
      await api.download(`/account/invoices/${invoice.id}/pdf`, `${invoice.number}.pdf`);
    } catch {
      // Factura nu are încă un PDF pregătit: intervalul se descarcă oricum ca CSV, util contabilității.
      try {
        await api.download('/analytics/export.csv?dataset=daily&days=30', `${invoice.number}.csv`);
        toast.success('Raport descărcat', 'Factura tipăribilă se generează la cerere; am descărcat raportul de consum pentru perioada facturată.');
      } catch (error) {
        toast.error('Descărcarea nu a reușit', errorMessage(error));
      }
    }
  }

  if (subscription.loading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid items-start gap-4 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Abonament"
        subtitle="Planul tău, limitele și facturile — toate modificabile oricând, fără a pierde date"
        actions={
          canManage ? (
            <Button variant="secondary" icon={<Icon name="tag" />} onClick={() => setPlanTarget(sortedPlans.find((plan) => plan.code !== current?.planCode) ?? null)}>
              Schimbă planul
            </Button>
          ) : undefined
        }
      />

      {subscription.error ? (
        <InlineAlert tone="danger" title="Abonamentul nu a putut fi încărcat">
          {subscription.error}
        </InlineAlert>
      ) : null}

      <div className="grid items-start gap-4 lg:grid-cols-3">
        {/* Planul curent */}
        <Card className="lg:col-span-2">
          <CardHeader
            title={current ? current.planName : 'Plan gratuit'}
            subtitle={current?.billingInterval === 'year' ? 'facturare anuală' : 'facturare lunară'}
            action={statusMeta ? <Badge tone={statusMeta.tone}>{statusMeta.label}</Badge> : null}
          />
          {current ? (
            <>
              <p className="nums font-display text-display-l text-ink-950">
                {current.priceCents === 0 ? 'Gratuit' : `${formatCurrency(current.priceCents, current.currency)} / lună`}
                {current.discountPercent ? <span className="ml-2 text-body-s font-normal text-success">−{current.discountPercent}% discount aplicat</span> : null}
              </p>
              <p className="mt-1 text-body-s text-ink-600">{statusMeta?.hint}</p>

              <dl className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="flex flex-col">
                  <dt className="text-caption text-ink-400">Perioada curentă</dt>
                  <dd className="text-body-s text-ink-950">
                    {formatDateTime(current.currentPeriodStart)} → {formatDateTime(current.currentPeriodEnd)}
                  </dd>
                </div>
                {current.trialEndsAt ? (
                  <div className="flex flex-col">
                    <dt className="text-caption text-ink-400">Proba se încheie</dt>
                    <dd className="text-body-s text-ink-950">{formatDateTime(current.trialEndsAt)}</dd>
                  </div>
                ) : null}
                <div className="flex flex-col">
                  <dt className="text-caption text-ink-400">Reînnoire</dt>
                  <dd className="text-body-s text-ink-950">
                    {current.cancelAtPeriodEnd ? 'se încheie la finalul perioadei' : 'automată'}
                  </dd>
                </div>
              </dl>

              {canManage ? (
                <div className="mt-5 flex flex-wrap gap-2">
                  {current.cancelAtPeriodEnd ? (
                    <Button loading={busy} onClick={() => void resumeSubscription()}>
                      Reia abonamentul
                    </Button>
                  ) : current.planCode !== 'free' ? (
                    <Button variant="ghost" className="text-danger" onClick={() => setCancelling(true)}>
                      Anulează la finalul perioadei
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            <EmptyState title="Niciun abonament" description="Alege un plan pentru a activa meniul QR, ecranele și statisticile." />
          )}
        </Card>

        {/* Consumul față de limite */}
        <Card>
          <CardHeader title="Consum" subtitle="Cât din planul tău folosești acum" />
          {usage.length ? (
            <ul className="flex flex-col gap-3.5">
              {usage.map((item) => {
                const ratio = item.limit > 0 ? item.used / item.limit : 0;
                const near = ratio >= 0.8;
                return (
                  <li key={item.key} className="flex flex-col gap-1.5">
                    <div className="flex items-baseline justify-between gap-2 text-body-s">
                      <span className="text-ink-800">{item.label}</span>
                      <span className={cx('nums', near ? 'font-medium text-warning' : 'text-ink-600')}>
                        {formatNumber(item.used)}/{formatNumber(item.limit)}
                      </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-surface-3">
                      <div
                        className={cx('h-full rounded-full', near ? 'bg-warning' : 'bg-brand-500')}
                        style={{ width: `${Math.min(100, Math.max(2, ratio * 100))}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-body-s text-ink-400">Consumul apare după ce planul este activ.</p>
          )}
          {usage.some((item) => item.limit > 0 && item.used / item.limit >= 0.8) ? (
            <InlineAlert tone="warning" title="Te apropii de o limită">
              Când limita este atinsă, funcțiile existente continuă să funcționeze — doar adăugarea de elemente noi
              este oprită până la trecerea pe un plan superior.
            </InlineAlert>
          ) : null}
        </Card>
      </div>

      {/* Facturi */}
      <Card className="mt-6">
        <CardHeader
          title="Facturi"
          subtitle="Emise automat la începutul fiecărei perioade; plata se face prin transfer bancar, cu numărul facturii ca referință"
          action={openInvoices.length ? <Badge tone="warning">{openInvoices.length} de achitat</Badge> : <Badge tone="success">la zi</Badge>}
        />
        {invoices.loading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-10" />
            ))}
          </div>
        ) : invoices.data?.invoices.length ? (
          <div className="-mx-5 overflow-x-auto px-5">
            <table className="w-full min-w-[620px] border-collapse text-body-s">
              <thead>
                <tr className="text-left text-caption uppercase tracking-wide text-ink-400">
                  <th className="pb-2 font-medium">Factură</th>
                  <th className="pb-2 font-medium">Perioadă</th>
                  <th className="pb-2 font-medium">Emisă</th>
                  <th className="pb-2 font-medium">Scadentă</th>
                  <th className="pb-2 text-right font-medium">Total</th>
                  <th className="pb-2 text-right font-medium">Stare</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {invoices.data.invoices.map((invoice) => {
                  const meta = INVOICE_STATUS[invoice.status];
                  const overdue = invoice.status === 'open' && invoice.dueAt && new Date(invoice.dueAt) < new Date();
                  return (
                    <tr key={invoice.id}>
                      <td className="py-2.5 pr-3 font-mono text-caption text-ink-950">{invoice.number}</td>
                      <td className="py-2.5 pr-3 text-ink-600">
                        {invoice.periodStart && invoice.periodEnd
                          ? `${formatDateTime(invoice.periodStart).slice(0, 10)} → ${formatDateTime(invoice.periodEnd).slice(0, 10)}`
                          : '—'}
                      </td>
                      <td className="py-2.5 pr-3 text-ink-600">{invoice.issuedAt ? formatDateTime(invoice.issuedAt) : '—'}</td>
                      <td className="py-2.5 pr-3 text-ink-600">{invoice.dueAt ? formatDateTime(invoice.dueAt) : '—'}</td>
                      <td className="nums py-2.5 text-right font-medium text-ink-950">{formatCurrency(invoice.totalCents, invoice.currency)}</td>
                      <td className="py-2.5 text-right">
                        <Badge tone={overdue ? 'danger' : meta.tone}>{overdue ? 'restantă' : meta.label}</Badge>
                      </td>
                      <td className="py-2.5 pl-3 text-right">
                        <div className="flex justify-end gap-2">
                          {invoice.status === 'open' && canManage ? (
                            <Button size="sm" variant="secondary" onClick={() => void openPayment(invoice)}>
                              Cum plătesc
                            </Button>
                          ) : null}
                          <Button size="sm" variant="ghost" onClick={() => void downloadInvoice(invoice)}>
                            Descarcă
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="Nicio factură încă"
            description="Facturile apar aici automat, la începutul fiecărei perioade facturate. Pe planul gratuit nu se emit facturi."
          />
        )}
      </Card>

      {/* Planuri disponibile */}
      <Card className="mt-6">
        <CardHeader title="Planuri disponibile" subtitle="Prețurile și limitele sunt configurabile din administrarea platformei" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {sortedPlans.map((plan) => {
            const isCurrent = plan.code === current?.planCode;
            return (
              <div
                key={plan.id}
                className={cx(
                  'flex flex-col gap-3 rounded-[var(--radius-md)] border p-4',
                  isCurrent ? 'border-brand-600 bg-brand-100/40' : 'border-ink-100 bg-surface',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display text-title text-ink-950">{plan.name}</h3>
                  {isCurrent ? <Badge tone="brand">planul tău</Badge> : null}
                </div>
                <p className="nums text-display-m font-display text-ink-950">
                  {plan.priceCents === 0 ? 'Gratuit' : `${formatCurrency(plan.priceCents, plan.currency)}`}
                  {plan.priceCents > 0 ? <span className="text-body-s font-normal text-ink-600"> / {plan.billingInterval === 'year' ? 'an' : 'lună'}</span> : null}
                </p>
                <p className="text-caption text-ink-600">{plan.description}</p>
                <ul className="flex flex-col gap-1.5 text-caption text-ink-800">
                  {(plan.features ?? []).slice(0, 6).map((feature) => (
                    <li key={feature} className="flex items-center gap-2">
                      <Icon name="check" size={14} className="text-success" />
                      {FEATURE_LABELS[feature] ?? feature}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto flex flex-col gap-2">
                  <span className="text-caption text-ink-400">
                    {plan.trialDays > 0 ? `${plan.trialDays} zile de probă` : 'fără perioadă de probă'}
                  </span>
                  {canManage && !isCurrent ? (
                    <Button size="sm" variant="secondary" onClick={() => setPlanTarget(plan)}>
                      Treci pe {plan.name}
                    </Button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Confirmare schimbare de plan */}
      <ConfirmDialog
        open={planTarget !== null}
        onClose={() => setPlanTarget(null)}
        onConfirm={() => void changePlan()}
        title={planTarget ? `Treci pe planul ${planTarget.name}?` : ''}
        description={
          planTarget
            ? `${planTarget.priceCents === 0 ? 'Plan gratuit' : `${formatCurrency(planTarget.priceCents, planTarget.currency)} / ${planTarget.billingInterval === 'year' ? 'an' : 'lună'}`}. Limitele se aplică imediat; datele existente rămân intacte, iar dacă depășești noile limite, elementele existente continuă să funcționeze.`
            : ''
        }
        confirmLabel="Schimbă planul"
        tone="primary"
        loading={busy}
      />

      <ConfirmDialog
        open={cancelling}
        onClose={() => setCancelling(false)}
        onConfirm={() => void cancelSubscription()}
        title="Anulezi abonamentul?"
        description="Accesul rămâne complet până la finalul perioadei plătite. Apoi contul trece pe planul gratuit — meniurile, codurile QR și datele rămân intacte."
        confirmLabel="Anulează la finalul perioadei"
        loading={busy}
      />

      {/* Instrucțiuni de plată */}
      <Modal
        open={paying !== null}
        onClose={() => setPaying(null)}
        title={paying ? `Plata facturii ${paying.number}` : ''}
        description="Datele de plată și pașii următori"
        size="sm"
        footer={
          <Button variant="secondary" onClick={() => setPaying(null)}>
            Am înțeles
          </Button>
        }
      >
        {payLoading ? (
          <Skeleton className="h-24" />
        ) : payment ? (
          <div className="flex flex-col gap-3">
            <p className="nums text-display-m font-display text-ink-950">{formatCurrency(payment.totalCents, payment.currency)}</p>
            {payment.dueAt ? <p className="text-body-s text-ink-600">Scadentă la {formatDateTime(payment.dueAt)}</p> : null}
            {payment.canPayOnline && payment.redirectUrl ? (
              <InlineAlert tone="info" title="Plata online este disponibilă">
                Te redirecționăm către pagina sigură a furnizorului de plăți pentru a finaliza tranzacția.
              </InlineAlert>
            ) : (
              <InlineAlert tone="info" title="Plată prin transfer bancar">
                {payment.instructions ?? payment.reason ?? 'Detaliile de plată apar aici.'}
              </InlineAlert>
            )}
            <p className="text-caption text-ink-400">
              Factura este marcată ca achitată automat la confirmarea încasării. Dacă ai plătit deja, scrie-ne
              numărul facturii și o confirmăm în aceeași zi lucrătoare.
            </p>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
